import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import type { Lang } from '@/types';
import { es } from './es';
import { en } from './en';

export const LANG_KEY = 'etc.lang';

function initialLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'es' || stored === 'en') return stored;
  } catch {
    /* storage unavailable */
  }
  return 'es'; // spec: Spanish is the default
}

void i18n.use(initReactI18next).init({
  resources: {
    es: { translation: es },
    en: { translation: en },
  },
  lng: initialLang(),
  fallbackLng: 'es',
  keySeparator: '.',
  nsSeparator: false,
  interpolation: {
    // the prototype's strings use single braces: "Cierra el {date}"
    prefix: '{',
    suffix: '}',
    escapeValue: false,
  },
  returnNull: false,
});

document.documentElement.lang = i18n.language;

/** Cambia el idioma de la pantalla.
 *
 *  El localStorage sigue aquí, pero ya no es dónde vive el idioma: es la
 *  copia que necesita la pantalla de entrada, que se dibuja antes de saber
 *  quién está entrando. El idioma de verdad está en la cuenta, y al
 *  hidratar se aplica con `persistir: false` — si no, el navegador de un
 *  equipo compartido acabaría imponiendo el idioma del último que entró. */
export function setLanguage(lang: Lang, opciones?: { persistir?: boolean }) {
  void i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  if (opciones?.persistir === false) return;
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* storage unavailable */
  }
  guardarEnLaCuenta(lang);
}

/** Quién guarda el idioma en la cuenta. Lo conecta la aplicación al
 *  arrancar; este módulo no puede llamar al `DataSource` porque el
 *  `DataSource` ya lo llama a él. Mismo patrón que `onWriteFailure`. */
let guardarEnLaCuenta: (lang: Lang) => void = () => {};
export const onLangChange = (fn: (lang: Lang) => void) => {
  guardarEnLaCuenta = fn;
};

/**
 * Mirrors the prototype's `t(key, vars)` signature so ported screens read
 * the same, and exposes the active language as a plain 'es' | 'en'.
 */
export function useI18n() {
  const { t, i18n: inst } = useTranslation();
  const lang = (inst.language?.startsWith('en') ? 'en' : 'es') as Lang;
  return {
    lang,
    t: (key: string, vars?: Record<string, string | number>) =>
      t(key, vars ?? {}) as string,
    setLang: setLanguage,
  };
}

/** Language-only subscription, for components that never render copy. */
export function useLang(): Lang {
  const { i18n: inst } = useTranslation();
  return (inst.language?.startsWith('en') ? 'en' : 'es') as Lang;
}

export default i18n;
